import { useQueryClient, useJobStatus } from 'shared-ui';
import { toast } from 'react-toastify';

import { acceptManyTerminals } from 'api/terminals/terminals';
import { JobIdResponse } from 'api/jobs/jobs.types';
import { TerminalStatusEnum } from 'api/terminals/terminals.enum';
import { useLocale } from 'hooks/useLocale/useLocale';
import { useSelectedTerminal } from 'hooks/useSelectedTerminal/useSelectedTerminal';
import { QueryKeyEnum } from 'core/global.enum';
import { AppMessages } from 'i18n/messages';
import { useMutation } from 'hooks/useMutation/useMutation';

import { AcceptMultiTerminals } from './AcceptMultiTerminals';
import { AcceptMultiTerminalsContainerProps } from './AcceptMultiTerminals.types';

export const AcceptMultiTerminalsContainer = ({
  checkedTerminals,
  setCheckedTerminals,
  disabled,
}: AcceptMultiTerminalsContainerProps) => {
  const queryClient = useQueryClient();
  const { formatMessage } = useLocale();
  const { selectedTerminal, setSelectedTerminal } = useSelectedTerminal();

  const handleJobComplete = () => {
    queryClient.invalidateQueries([QueryKeyEnum.TERMINALS]);
    toast.dark(
      formatMessage({
        id: AppMessages['devices.multiAction.accept.successMessage'],
      }),
    );
    setCheckedTerminals([]);
    selectedTerminal && setSelectedTerminal({ ...selectedTerminal, status: TerminalStatusEnum.OK });
  };

  const handleJobError = (error: string) => {
    toast.error(formatMessage({ id: AppMessages['common.error'] }) || `Error: ${error}`);
  };

  const jobStatus = useJobStatus({
    useGlobalModal: true,
    autoClose: false,
    onComplete: handleJobComplete,
    onError: handleJobError,
    showItemCount: true,
    customCompleteMessage: formatMessage({ id: AppMessages['devices.multiAction.accept.result.success'] }),
    customFailedMessage: formatMessage({ id: AppMessages['devices.multiAction.accept.result.failed'] }),
    customActiveMessage: formatMessage({ id: AppMessages['devices.multiAction.accept.result.processing'] }),
    title: formatMessage({ id: AppMessages['devices.multiAction.accept.progressTitle'] }),
  });

  const { mutate: onAcceptManyTerminals } = useMutation(acceptManyTerminals, {
    onSuccess: (response: JobIdResponse) => {
      if (!response || !response.jobId) {
        toast.error(formatMessage({ id: AppMessages['common.error'] }));
        return;
      }
      jobStatus.startJob(response.jobId);
    },
    onError: (error: unknown) => {
      const errorObj = error as Record<string, unknown>;
      toast.error(
        formatMessage({ id: AppMessages['common.error'] }) || `Error: ${errorObj?.message || 'Unknown error'}`,
      );
    },
  });

  const onSubmit = () => {
    onAcceptManyTerminals(checkedTerminals.map((terminal) => terminal.id));
  };

  return <AcceptMultiTerminals onSubmit={onSubmit} disabled={disabled} />;
};
